import React from 'react';
import { languages } from '../../../data/languages';
import { KnowledgeBaseFile } from '../../../lib/database';

const models = [
    // Google Models
    { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', provider: 'google', description: 'Enhanced thinking and reasoning, multimodal understanding, advanced coding, and more', cost: '$3.50/1M tokens', context: '2M context', maxTokens: 65536, pages: '~130 pages', supports: 'Audio, images, videos, text, and PDF', thinkingMin: 128, thinkingMax: 32768, thinkingOffAllowed: false },
    { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', provider: 'google', description: 'Adaptive thinking, cost efficiency', cost: '$0.075/1M tokens', context: '1M context', maxTokens: 32768, pages: '~65 pages', supports: 'Audio, images, videos, and text', thinkingMin: 1, thinkingMax: 24576, thinkingOffAllowed: true },
    { id: 'gemini-2.5-flash-lite', name: 'Gemini 2.5 Flash-Lite', provider: 'google', description: 'Most cost-efficient model supporting high throughput', cost: '$0.0375/1M tokens', context: '1M context', maxTokens: 16384, pages: '~32 pages', supports: 'Text, image, video, audio', thinkingMin: 512, thinkingMax: 24576, thinkingOffAllowed: true }
  ];

  const colors = [
    { id: 'bg-netlify-teal', name: 'Teal', color: 'bg-netlify-teal' },
    { id: 'bg-netlify-blue', name: 'Blue', color: 'bg-netlify-blue' },
    { id: 'bg-netlify-purple', name: 'Purple', color: 'bg-netlify-purple' },
    { id: 'bg-netlify-mint', name: 'Mint', color: 'bg-netlify-mint' },
    { id: 'bg-amber-500', name: 'Amber', color: 'bg-amber-500' },
    { id: 'bg-rose-500', name: 'Rose', color: 'bg-rose-500' }
  ];

  const thinkingModes = [
    { budget: -1, name: 'Auto', description: 'Il modello sceglie dinamicamente il budget per il ragionamento.' },
    { budget: 0, name: 'Off', description: 'Standard response mode without extra reasoning.' },
    { budget: 8192, name: 'Fast', description: 'Budget limitato per ragionamenti rapidi e compiti semplici.' },
    { budget: 16384, name: 'Intermediate', description: 'Budget bilanciato per la maggior parte dei compiti che richiedono ragionamento.' },
    { budget: 32768, name: 'Deep', description: 'Budget elevato per problemi complessi che richiedono un ragionamento profondo.' }
  ];


interface Step1BasicInfoProps {
  formData: {
    name: string;
    description: string;
    language: string[];
    color: string;
    model: string;
    thinkingBudget: number;
    knowledgeBase: KnowledgeBaseFile[];
  };
  updateFormData: (key: string, value: any) => void;
}

const Step1BasicInfo: React.FC<Step1BasicInfoProps> = ({ formData, updateFormData }) => {
    const [showLanguageDropdown, setShowLanguageDropdown] = React.useState(false);
    const [languageSearch, setLanguageSearch] = React.useState('');
    const [showThinkingDropdown, setShowThinkingDropdown] = React.useState(false);

    const filteredLanguages = languages.filter(lang => 
        lang.name.toLowerCase().includes(languageSearch.toLowerCase()) ||
        lang.native.toLowerCase().includes(languageSearch.toLowerCase()) ||
        lang.code.toLowerCase().includes(languageSearch.toLowerCase())
      );

      React.useEffect(() => {
        const selectedModel = models.find(m => m.id === formData.model);
        if (!selectedModel) return;
    
        const currentBudget = formData.thinkingBudget;
        let isValid = false;
    
        if (currentBudget === -1) {
            isValid = true;
        } else if (currentBudget === 0) {
            isValid = selectedModel.thinkingOffAllowed;
        } else if (currentBudget > 0) {
            isValid = currentBudget >= selectedModel.thinkingMin && currentBudget <= selectedModel.thinkingMax;
        }
    
        if (!isValid) {
            updateFormData('thinkingBudget', -1);
        }
      }, [formData.model, formData.thinkingBudget, updateFormData]);

      React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
          if (showLanguageDropdown) {
            const target = event.target as HTMLElement;
            if (!target.closest('.language-dropdown')) {
              setShowLanguageDropdown(false);
            }
          }
          if (showThinkingDropdown) {
            const target = event.target as HTMLElement;
            if (!target.closest('.thinking-dropdown')) {
              setShowThinkingDropdown(false);
            }
          }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }, [showLanguageDropdown, showThinkingDropdown]);

      const handleLanguageSelect = (langCode: string) => {
        const newLanguages = formData.language.includes(langCode)
          ? formData.language.filter(l => l !== langCode)
          : [...formData.language, langCode];
        updateFormData('language', newLanguages);
      };

      const selectedModel = models.find(m => m.id === formData.model);

      const isThinkingModeDisabled = (mode: typeof thinkingModes[0]) => {
        if (!selectedModel) return true;
    
        if (mode.budget === -1) return false; // Dynamic is always allowed
        if (mode.budget === 0) return !selectedModel.thinkingOffAllowed;
        if (mode.budget > 0) {
          return mode.budget < selectedModel.thinkingMin || mode.budget > selectedModel.thinkingMax;
        }
        return true;
      };

  return (
    <div className="space-y-8">
      {/* Basic Information */}
      <div className="bg-white rounded-xl p-4 sm:p-6 space-y-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Agent Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => updateFormData('name', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal focus:border-transparent text-lg"
            placeholder="e.g., Customer Support Agent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => updateFormData('description', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal focus:border-transparent resize-none"
            rows={3}
            placeholder="Briefly describe what this agent does..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
            <div className="relative language-dropdown">
              <button
                type="button"
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal focus:border-transparent text-left bg-white flex items-center justify-between"
              >
                <div className="flex flex-wrap gap-2">
                  {formData.language.length === 0 && <span className="text-gray-500">Select languages...</span>}
                  {formData.language.map(langCode => (
                    <span key={langCode} className="flex items-center gap-2 bg-gray-100 text-gray-700 rounded-full px-3 py-1 text-sm">
                      {languages.find(l => l.code === langCode)?.name}
                    </span>
                  ))}
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>
              
              {showLanguageDropdown && (
                <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg">
                  <div className="p-3 border-b border-gray-200">
                    <input
                      type="text"
                      placeholder="Search languages..."
                      value={languageSearch}
                      onChange={(e) => setLanguageSearch(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal focus:border-transparent text-sm"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {filteredLanguages.map((language) => (
                      <button
                        key={language.code}
                        type="button"
                        onClick={() => handleLanguageSelect(language.code)}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 ${
                          formData.language.includes(language.code) ? 'bg-netlify-teal bg-opacity-10 text-netlify-teal' : 'text-gray-700'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded bg-gray-100 flex items-center justify-center text-xs font-medium ${
                          formData.language.includes(language.code) ? 'bg-netlify-teal text-white' : 'text-gray-600'
                        }`}>
                          {language.code.toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium">{language.name}</div>
                          <div className="text-sm text-gray-500">{language.native}</div>
                        </div>
                        {formData.language.includes(language.code) && (
                          <svg className="w-4 h-4 text-netlify-teal ml-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Agent Color</label>
            <div className="grid grid-cols-6 gap-2">
              {colors.map((color) => (
                <label key={color.id} className="cursor-pointer group">
                  <input
                    type="radio"
                    name="color"
                    value={color.id}
                    checked={formData.color === color.id}
                    onChange={(e) => updateFormData('color', e.target.value)}
                    className="sr-only peer"
                  />
                  <div className={`w-10 h-10 rounded-lg ${color.color} peer-checked:ring-4 peer-checked:ring-gray-300 group-hover:scale-110 transition-all`}></div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* AI Model Selection - Compact */}
      <div className="bg-white rounded-xl p-4 sm:p-6">
        <div className="flex items-center space-x-2 mb-4">
          <div className="w-6 h-6 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-xs font-bold text-white">G</span>
          </div>
          <h3 className="text-lg font-medium text-gray-900">AI Model</h3>
        </div>
        <p className="text-gray-600 mb-6">Choose the AI model that powers your agent</p>
        
        <div className="space-y-3">
          {models.map(model => (
            <label key={model.id} className="group cursor-pointer block">
              <input 
                type="radio" 
                name="model" 
                value={model.id} 
                className="sr-only peer" 
                checked={formData.model === model.id}
                onChange={(e) => updateFormData('model', e.target.value)}
              />
              <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 peer-checked:border-blue-600 peer-checked:bg-blue-50 hover:border-gray-300 transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="font-semibold text-gray-900">{model.name}</h4>
                      {model.id === 'gemini-2.5-flash' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Recommended</span>
                      )}
                      {model.id === 'gemini-2.5-pro' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Premium</span>
                      )}
                      {model.id === 'gemini-2.5-flash-lite' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Efficient</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2 hidden sm:block">{model.description}</p>
                    <div className="hidden sm:flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span>{model.context}</span>
                      <span>{model.cost}</span>
                      <span>Max: {model.pages}</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full peer-checked:border-blue-600 peer-checked:bg-blue-600 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full opacity-0 peer-checked:opacity-100"></div>
                    </div>
                  </div>
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>
      
      {/* Thinking Mode Dropdown */}
      <div className="bg-white rounded-xl p-4 sm:p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-2">Thinking Mode</h4>
        <p className="text-gray-600 mb-4 hidden sm:block">Enable step-by-step reasoning for complex tasks. Higher budgets allow for more complex reasoning but may increase response time and cost.</p>
        <div className="relative thinking-dropdown">
          <button
            type="button"
            onClick={() => setShowThinkingDropdown(!showThinkingDropdown)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-netlify-teal focus:border-transparent text-left bg-white flex items-center justify-between"
          >
            <div>
              <span className="font-medium">{thinkingModes.find(m => m.budget === formData.thinkingBudget)?.name}</span>
              <span className="text-gray-500 ml-2 hidden sm:inline">- {thinkingModes.find(m => m.budget === formData.thinkingBudget)?.description}</span>
            </div>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
          
          {showThinkingDropdown && (
            <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg bottom-full mb-2">
              <div className="max-h-60 overflow-y-auto">
                {thinkingModes.map((mode) => {
                  const isDisabled = isThinkingModeDisabled(mode);
                  return (
                    <button
                      key={mode.budget}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => {
                        if (isDisabled) return;
                        updateFormData('thinkingBudget', mode.budget);
                        setShowThinkingDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 relative ${
                        formData.thinkingBudget === mode.budget ? 'bg-netlify-teal bg-opacity-10 text-netlify-teal' : 'text-gray-700'
                      } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="font-medium">{mode.name}</div>
                      <div className="text-sm text-gray-500">{mode.description}</div>
                      {formData.thinkingBudget === mode.budget && (
                        <svg className="w-4 h-4 text-netlify-teal absolute top-1/2 right-4 transform -translate-y-1/2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Step1BasicInfo;